import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import oktaApiClient from '../okta/frontend/oktaApiClient';
import yardsData from '../assets/yards.json';

const API_BASE = process.env.REACT_APP_API_SERVER_URL;

// Returns unique yard options from the device list
function getYardOptions(devices) {
  const seen = new Set();
  const opts = [{ value: '', label: 'All Yards' }];
  devices.forEach(d => {
    const key = d.yard_number ? `Yard ${d.yard_number}` : null;
    if (key && !seen.has(key)) {
      seen.add(key);
      opts.push({ value: String(d.yard_number), label: key });
    }
  });
  return opts.sort((a, b) => {
    if (!a.value) return -1;
    if (!b.value) return 1;
    return Number(a.value) - Number(b.value);
  });
}

export default function AddDevicesToGroupModal({
  group,
  existingMembers = [],
  maxAdd = 30,
  onClose,
  onAdded,
  returnObjects = false, // if true, pass full device objects; if false, pass androidIds only
}) {
  const [allDevices, setAllDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [yardFilter, setYardFilter] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const existingIds = useMemo(() => new Set(existingMembers.map(m => m.android_id)), [existingMembers]);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API_BASE}/maiApi/latest-android-data`, {
          params: { limit: 2000 },
        });
        const raw = Array.isArray(res.data) ? res.data
          : Array.isArray(res.data?.data) ? res.data.data
          : [];

        // Deduplicate by android_id (keep latest)
        const map = new Map();
        raw.forEach(d => {
          if (!map.has(d.android_id) || new Date(d.timestamp) > new Date(map.get(d.android_id).timestamp)) {
            map.set(d.android_id, d);
          }
        });
        setAllDevices(Array.from(map.values()));
      } catch (err) {
        setError('Failed to load devices.');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const yardOptions = useMemo(() => getYardOptions(allDevices), [allDevices]);

  const isOffline = (d) => {
    if (!d.timestamp) return true;
    const diff = (Date.now() - new Date(d.timestamp).getTime()) / 60000;
    return diff > 30;
  };

  const available = useMemo(() => {
    return allDevices.filter(d => {
      if (existingIds.has(d.android_id)) return false;
      if (yardFilter && String(d.yard_number) !== yardFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          d.android_id?.toLowerCase().includes(q) ||
          d.device_friendly_name?.toLowerCase().includes(q) ||
          String(d.yard_number || '').includes(q)
        );
      }
      return true;
    });
  }, [allDevices, existingIds, yardFilter, search]);

  const toggleSelect = (androidId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(androidId)) {
        next.delete(androidId);
      } else {
        if (next.size >= maxAdd) return prev;
        next.add(androidId);
      }
      return next;
    });
  };

  const wouldExceedCap = selected.size + existingMembers.length > 30;

  const handleAdd = async () => {
    if (!selected.size) return;
    setSaving(true);
    setError('');
    try {
      const androidIds = Array.from(selected);
      if (group.group_id !== '__new__') {
        await oktaApiClient.post(`/speed-test-groups/${group.group_id}/devices`, { androidIds });
      }
      if (returnObjects) {
        const devObjs = allDevices.filter(d => selected.has(d.android_id));
        onAdded(devObjs);
      } else {
        onAdded(androidIds);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add devices.');
      setSaving(false);
    }
  };

  const currentTotal = existingMembers.length + selected.size;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Add Devices</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Adding to: <span className="font-medium">{group.group_name}</span>
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>

        {/* Filters */}
        <div className="px-6 py-3 border-b bg-gray-50 flex flex-wrap gap-3 items-center">
          <input
            type="text"
            placeholder="Search device ID or name..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 min-w-[180px] px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <select
            value={yardFilter}
            onChange={e => setYardFilter(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {yardOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <span className={`text-sm font-medium ${wouldExceedCap ? 'text-red-600' : 'text-gray-600'}`}>
            {selected.size > 0 && (
              <>Selecting {selected.size} — group will be {currentTotal}/30{wouldExceedCap ? ' ⚠ over cap' : ''}</>
            )}
          </span>
        </div>

        {/* Device list */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-500 text-sm">Loading devices...</div>
          ) : error ? (
            <div className="p-6 text-center text-red-600 text-sm">{error}</div>
          ) : available.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">
              {search || yardFilter ? 'No devices match your filters.' : 'All available devices are already in this group.'}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-100 text-gray-600 sticky top-0">
                <tr>
                  <th className="p-2 w-10"></th>
                  <th className="p-2 text-left">Device ID</th>
                  <th className="p-2 text-left">Name</th>
                  <th className="p-2 text-center">Yard</th>
                  <th className="p-2 text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {available.map(d => {
                  const offline = isOffline(d);
                  const isSelected = selected.has(d.android_id);
                  const yard = yardsData.find(y => String(y.Yard) === String(d.yard_number));
                  const yardLabel = yard ? `${d.yard_number} — ${yard.City}` : d.yard_number ? `Yard ${d.yard_number}` : '--';

                  return (
                    <tr
                      key={d.android_id}
                      className={`border-t cursor-pointer transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                      onClick={() => toggleSelect(d.android_id)}
                    >
                      <td className="p-2 text-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(d.android_id)}
                          onClick={e => e.stopPropagation()}
                        />
                      </td>
                      <td className="p-2 font-mono text-xs text-gray-700">{d.android_id}</td>
                      <td className="p-2 text-gray-600">{d.device_friendly_name || '--'}</td>
                      <td className="p-2 text-center text-gray-600">{yardLabel}</td>
                      <td className="p-2 text-center">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${offline ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-800'}`}>
                          {offline ? 'Offline' : 'Online'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50 rounded-b-lg">
          <p className="text-xs text-gray-400">
            {existingMembers.length} already in group · {30 - existingMembers.length} slots remaining
          </p>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">
              Cancel
            </button>
            <button
              onClick={handleAdd}
              disabled={!selected.size || wouldExceedCap || saving}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-40 transition-colors"
            >
              {saving ? 'Adding...' : `Add ${selected.size > 0 ? selected.size + ' ' : ''}Device${selected.size !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
